import React from "react";
import {
  useSetSettingActiveMutation,
  useSetSettingDefaultMutation,
  useUpdateGravitySettingMutation,
  useVerifyGravityConfigurationMutation,
} from "../../../redux/reducer/features/Gravity/GravitySettingSlice";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { GravitySetting } from "../../../models/features/Gravity/Gravity";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { PaymentType } from "../../../models/features/ShoppingPortal/ShoppingPortalDto";
import { useDispatch } from "react-redux";
import { setPaymentType } from "../../../redux/reducer/features/Auth/authSlice";
import { useSetPaymentTypeMutation } from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { AddGravitySettingDialog } from "./AddGravitySettingDialog";
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
export interface GravitySettingsListProps {
  paymentSettings: GravitySetting[];
  handleSettingDelete: (settingId: string) => void;
  handleRefetchSetting: () => void;
}

export const GravitySettingsList: React.FunctionComponent<
  GravitySettingsListProps
> = (props) => {
  const [setSettingActiveMutation, setSettingActiveMutationResult] =
    useSetSettingActiveMutation();
  const [setPaymentTypeMutation, setPaymnetTypeMutationResult] =
    useSetPaymentTypeMutation();
  const [setSettingDefaultMutation, setSettingDefaultMutationResult] =
    useSetSettingDefaultMutation();
  const [gravitySettings, setGravitySettings] = React.useState<
    GravitySetting[]
  >(props.paymentSettings);
  const [updateSettingMutation, updateSettingMutationResult] =
    useUpdateGravitySettingMutation();
  const [verifyConfigurationMutation, verifyConfigurationMutationResult] =
    useVerifyGravityConfigurationMutation();
  const [selectedSettingId, setSelectedSettingId] = React.useState<any>(null);
  const [selectedRow, setSelectedRow] = React.useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [verificationMessage, setVerificationMessage] = React.useState("");
  const [openUpdate, setOpenUpdate] = React.useState(false);
  const [isVerified, setIsVerified] = React.useState(false);
  const [showVerification, setShowVerification] = React.useState(false);
  const dispatch = useDispatch();
  const paymentType = useSelector(
    (state: AppState) => state?.authReducer?.dmsSetup?.paymentType
  );

  const handleClose = () => {
    setOpenUpdate(false);
    setDeleteConfirm(false);
  };
  const handleSetDefault = (id: any, value: boolean) => {
    setGravitySettings(
      gravitySettings.map((item) =>
        item.id === id
          ? { ...item, isDefault: value }
          : { ...item, isDefault: false }
      )
    );
    setSettingDefaultMutation({ data: { id: id, value: value } }).then(
      (res: any) => {
        if (res && res.data) {
          props.handleRefetchSetting();
        }
      }
    );
  };
  const handleSetActive = (id: any, value: boolean) => {
    setGravitySettings(
      gravitySettings.map((item) =>
        item.id === id ? { ...item, isActive: value } : item
      )
    );
    setSettingActiveMutation({ data: { id: id, value: value } }).then(
      (res: any) => {
        if (res && res.data) {
          props.handleRefetchSetting();
        }
      }
    );
  };

  const handleSettingUpdate = (values: GravitySetting) => {
    setErrorMessage("");
    updateSettingMutation({
      data: {
        ...values,
      },
    }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 200) {
          setOpenUpdate(false);
          props.handleRefetchSetting();
        } else {
          setErrorMessage(res.data.message);
        }
      }
    });
  };
  const handleCloseAlert = () => {
    setShowVerification(false);
  };
  const handleVerifyConfiguration = (setting: GravitySetting) => {
    verifyConfigurationMutation({
      data: { oid: setting.oid, authToken: setting.authToken },
    }).then((res: any) => {
      if (res && res.data) {
        setShowVerification(true);
        if (res.data.status === 200) {
          setIsVerified(true);
          setVerificationMessage(
            "This setting is verified and Valid to make payments."
          );
        } else {
          setIsVerified(false);
          setVerificationMessage(res.data.message);
        }
      }
    });
  };
  const handleDelete = () => {
    setDeleteConfirm(false);
    props.handleSettingDelete(selectedSettingId);
  };
  const handleChangePaymentType = (value: string) => {
    dispatch(setPaymentType({ value: value }));
    if (value === "Gravity") {
      setPaymentTypeMutation({ type: PaymentType.Gravity });
    } else if (value === "PayPal") {
      setPaymentTypeMutation({ type: PaymentType.PayPal });
    }
  };
  React.useEffect(() => {
    setGravitySettings(props.paymentSettings);
  }, [props.paymentSettings]);
  return (
    <>
      <Typography sx={{ fontSize: "14px", mb: 1 }} component="span">
        Gravity Settings
      </Typography>
      <Switch
        aria-labelledby="demo-controlled-radio-buttons-group"
        checked={paymentType === "Gravity"}
        name="paymentType"
        onChange={(e) => {
          console.log(e);
          if (e.target.checked) {
            handleChangePaymentType("Gravity");
          } else {
            handleChangePaymentType("PayPal");
          }
        }}
      />
      <TableContainer sx={{ boxShadow: 4 }} component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Setting Name</StyledTableCell>
              <StyledTableCell>OID</StyledTableCell>
              <StyledTableCell>Auth Token</StyledTableCell>
              <StyledTableCell align="center">Default</StyledTableCell>
              <StyledTableCell align="center">Active</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
              <StyledTableCell align="center">Verify</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gravitySettings && gravitySettings.length > 0 ? (
              gravitySettings.map((row) => (
                <StyledTableRow key={row.id}>
                  <StyledTableCell>{row.settingName}</StyledTableCell>
                  <StyledTableCell align="left">{row.oid}</StyledTableCell>
                  <StyledTableCell
                    sx={{
                      maxWidth: 130,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    align="left"
                  >
                    {row.authToken}
                  </StyledTableCell>

                  <StyledTableCell align="center">
                    <Switch
                      size="small"
                      checked={row?.isDefault}
                      name="isDefault"
                      onChange={(e) => {
                        handleSetDefault(row.id, e.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Switch
                      size="small"
                      checked={row?.isActive}
                      name="isActive"
                      onChange={(e) => {
                        handleSetActive(row.id, e.target.checked);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <IconButton
                      id="basic-button"
                      onClick={(n) => {
                        setSelectedRow(row);
                        setOpenUpdate(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    |
                    <IconButton
                      id="basic-button"
                      onClick={(n) => {
                        setSelectedSettingId(row.id);
                        setDeleteConfirm(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={(n) => {
                        setSelectedRow(row);
                        handleVerifyConfiguration(row);
                      }}
                      endIcon={
                        verifyConfigurationMutationResult.isLoading &&
                        row.id === selectedRow.id ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : (
                          <></>
                        )
                      }
                      disabled={
                        verifyConfigurationMutationResult.isLoading &&
                        row.id === selectedRow.id
                      }
                    >
                      Verify
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell align="center" colSpan={7}>
                  No Rows to Show
                </StyledTableCell>
              </StyledTableRow>
            )}
            {showVerification ? (
              <StyledTableRow>
                <StyledTableCell colSpan={8}>
                  <Alert
                    onClose={handleCloseAlert}
                    severity={isVerified ? "success" : "error"}
                  >
                    {verificationMessage}
                  </Alert>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              ""
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteConfirm} onClose={handleClose} fullWidth>
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Confirm to Delete
        </DialogTitle>
        <DialogContent>Are you sure to delete this Setting?</DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setDeleteConfirm(false);
            }}
          >
            No
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (selectedSettingId) handleDelete();
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <AddGravitySettingDialog
        isEdit={true}
        isOpen={openUpdate}
        errorMessage={errorMessage}
        isLoading={updateSettingMutationResult.isLoading}
        handleSettingSubmit={handleSettingUpdate}
        onClose={() => {
          setOpenUpdate(false);
          setErrorMessage("");
        }}
        setting={selectedRow}
      ></AddGravitySettingDialog>
    </>
  );
};
